import { appUrl } from '@astro/config'
import { useCourseCatalog } from '../useDashboard'
import EnrolledCoursesSections from './EnrolledCoursesSections'
import cover1 from '../assets/course-1.webp'
import cover2 from '../assets/course-2.webp'
import cover3 from '../assets/course-3.webp'
import cover4 from '../assets/course-4.webp'

/* ===================== ACADEMY / COURSES =====================
   Fully live: the browse row comes from GET /courses (published
   catalog) and the two enrolment sections below it from
   GET /enrollments — no demo rows. Both show pulsing skeletons
   while loading; course pages open in the education app
   (/classes/:idOrSlug). The enrolment sections live in
   EnrolledCoursesSections because the instructor Academy screen
   reuses them for accounts that also hold the student role. */

const courseHref = (ref) => appUrl('education', ref != null ? `/classes/${ref}` : '/classes')

/** Not every live course has a coverImage uploaded — cycle local cover art
 * by list position for the gaps, same as the education app's catalog. */
const FALLBACK_COVERS = [cover1, cover2, cover3, cover4]
const coverFor = (img, i) => img || FALLBACK_COVERS[i % FALLBACK_COVERS.length]

function BrowseSkeletonCard() {
  return (
    <div className="browse-card flex flex-col animate-pulse" aria-hidden="true">
      <div className="h-28 bg-stone-100" />
      <div className="p-4 flex flex-col flex-1 space-y-2">
        <div className="h-2.5 w-16 bg-stone-100 rounded-full" />
        <div className="h-3.5 w-5/6 bg-stone-100 rounded-full" />
        <div className="flex items-center gap-1.5">
          <div className="w-4.5 h-4.5 rounded-full bg-stone-100 shrink-0" />
          <div className="h-2.5 w-20 bg-stone-100 rounded-full" />
        </div>
        <div className="h-2.5 w-2/3 bg-stone-100 rounded-full" />
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-stone-100">
          <div className="h-4 w-16 bg-stone-100 rounded-full" />
          <div className="h-8 w-16 bg-stone-100 rounded-lg" />
        </div>
      </div>
    </div>
  )
}

export default function CoursesTab() {
  const { courses, hasCourses, isLoading: catalogLoading, isError: catalogError, refetch } = useCourseCatalog(4)

  return (
    <div className="space-y-6">

      {/* ── Browse the live catalog ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
          <span className="eyebrow text-amber-700">Vedic Academy</span>
          <a href={courseHref()} className="text-[10px] uppercase font-bold tracking-wider text-goldAccent hover:underline">
            View all courses
          </a>
        </div>
        <h3 className="text-xl text-astroDark mb-5 font-royal">Explore Certified Courses</h3>

        {catalogError ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t load the catalogue</p>
            <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching live courses. Please try again.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : !catalogLoading && !hasCourses ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🎓</div>
            <p className="text-base font-semibold text-astroDark mb-1">No courses published yet</p>
            <p className="text-sm text-stone-400">New Vedic Academy courses will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {catalogLoading
              ? Array.from({ length: 4 }, (_, i) => <BrowseSkeletonCard key={i} />)
              : courses.map((c, i) => (
                <div key={c.key} className="browse-card flex flex-col">
                  <a href={courseHref(c.courseRef)} className="relative block h-28 overflow-hidden bg-amber-100">
                    <img
                      src={coverFor(c.img, i)}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_COVERS[i % FALLBACK_COVERS.length] }}
                    />
                    <span className="absolute top-2 left-2 text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/90 text-amber-700">{c.badge}</span>
                  </a>
                  <div className="p-4 flex flex-col flex-1">
                    <span className="text-[9px] uppercase tracking-wider text-stone-400 font-semibold truncate">{c.category}</span>
                    {/* Reserve two title lines so short titles don't pull the rows below up. */}
                    <p className="text-[13px] font-semibold text-astroDark leading-snug mt-1 line-clamp-2 min-h-9">{c.title}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 min-h-5">
                      {c.instructor && (
                        <>
                          {c.instructorPhoto ? (
                            <img src={c.instructorPhoto} alt="" loading="lazy" className="w-4.5 h-4.5 rounded-full object-cover shrink-0" />
                          ) : (
                            <span className="w-4.5 h-4.5 rounded-full bg-amber-100 text-amber-700 text-[8px] font-bold flex items-center justify-center shrink-0">
                              {c.instructor.charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className="text-[10px] text-stone-500 truncate">{c.instructor}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 pb-3 text-[10px] text-stone-500">
                      <span>👥 {c.students.toLocaleString('en-IN')} enrolled</span>
                      {c.reviews > 0 && (
                        <>
                          <span>·</span>
                          <span>⭐ {c.reviews.toLocaleString('en-IN')} reviews</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-stone-100">
                      <div>
                        <span className="num text-base font-bold text-astroDark">{c.priceLabel}</span>{' '}
                        {c.oldPriceLabel && <span className="text-[10px] line-through text-stone-400">{c.oldPriceLabel}</span>}
                      </div>
                      <a
                        href={courseHref(c.courseRef)}
                        className="text-[9px] font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark"
                      >
                        Enroll
                      </a>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* ── My Enrolled Courses + My Enrolled Live Courses ── */}
      <EnrolledCoursesSections />
    </div>
  )
}
